import React, { useCallback, useEffect, useMemo } from "react";
import { Button, Modal, Row, Typography, Form, FormInstance, Col, Input, Select, Checkbox } from "antd";
import FormLabel from "src/components/AntdCustomPart/FormLabel/FormLabel";
import { generateErrorMessage } from "src/utils/error-message";
import { AreaProps } from "src/entity/area/area.entity";
import { ResponseSettingWasteWaterTreatment } from "src/entity/settings/waste-water";
import CustomFormDatePicker from "src/components/AntdCustomPart/CustomFormDatePicker/CustomFormDatePicker";
import { EmissionObject, EmissionObjectEnum } from "src/entity/common/unit.constant";
import { CloseCircleOutlined } from "@ant-design/icons";
import { ResponseSetupWasteWater } from "src/entity/waste-water/setup-information.entity";

const { Text } = Typography;

interface InformationModalProps {
  submitSetupWasteWater: () => void;
  loadingManageWasteWater: boolean;
  visibleModal: boolean;
  closeModal: () => void;
  errorWasteWater: string | null;
  formSetupWasteWater: FormInstance;
  clearErrorWasteWater: (key: string) => void;
  dataAreaInCompany: AreaProps[];
  dataSettingWasteWater: ResponseSettingWasteWaterTreatment[];
}

const InformationModal: React.FC<InformationModalProps> = (props) => {
  const {
    submitSetupWasteWater,
    loadingManageWasteWater,
    closeModal,
    visibleModal,
    errorWasteWater,
    formSetupWasteWater,
    clearErrorWasteWater,
    dataAreaInCompany,
    dataSettingWasteWater,
  } = props;

  const optionsAreaInCompany = useMemo(() => {
    return dataAreaInCompany.map((item) => ({
      label: item.areaName,
      value: item._id,
    }));
  }, [dataAreaInCompany]);

  const optionsSettingWasteWater = useMemo(() => {
    return dataSettingWasteWater.map((item) => ({
      label: item.emissionControl,
      value: item._id,
    }));
  }, [dataSettingWasteWater]);

  const onSelectEmissionControl = useCallback(
    (val: string) => {
      if (val) {
        const settingSlot = dataSettingWasteWater.find((item) => item._id === val);
        // console.log("settingSlot?.efficiency : ", typeof settingSlot?.efficiency);
        formSetupWasteWater.setFieldValue("efficiency", settingSlot?.efficiency);
      } else {
        formSetupWasteWater.setFieldValue("efficiency", "0");
      }
    },
    [dataSettingWasteWater],
  );

  useEffect(() => {
    const emissionControl = formSetupWasteWater.getFieldValue("emissionControl");
    // console.log("emissionControl :: ", emissionControl);
    if (!emissionControl) formSetupWasteWater.setFieldValue("emissionControl", "");
  }, []);

  const exceptionDateRanges = Form.useWatch("controlExceptionDate", formSetupWasteWater);

  return (
    <Modal
      title="WW Information"
      open={visibleModal}
      width={1000}
      onCancel={closeModal}
      footer={
        <Row className="w-full" justify={"end"}>
          <Button key="submit" type="primary" loading={loadingManageWasteWater} onClick={submitSetupWasteWater}>
            Save
          </Button>
        </Row>
      }
      maskClosable={false}
      centered
      destroyOnClose
    >
      {errorWasteWater && (
        <Row className="w-full" justify={"center"}>
          <Text type="danger">{errorWasteWater}</Text>
        </Row>
      )}
      <Form
        size="middle"
        name={"setup-ww-information"}
        layout="vertical"
        autoComplete="off"
        form={formSetupWasteWater}
        requiredMark={false}
        onFieldsChange={(_) => {
          if (errorWasteWater) {
            clearErrorWasteWater("errorWasteWater");
            formSetupWasteWater.setFields([
              {
                name: "wasteWaterUnit",
                errors: undefined,
              },
              {
                name: "area",
                errors: undefined,
              },
            ]);
          }
        }}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12} lg={12}>
            <Form.Item
              label={<FormLabel label={"Wastewater Unit"} required />}
              name="wasteWaterUnit"
              rules={[
                {
                  required: true,
                  message: generateErrorMessage("Wastewater Unit"),
                },
              ]}
            >
              <Input maxLength={50} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={12}>
            <Form.Item
              label={<FormLabel label={"Area"} required />}
              name="area"
              rules={[
                {
                  required: true,
                  message: generateErrorMessage("Area"),
                },
              ]}
            >
              <Select options={optionsAreaInCompany} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={12}>
            <Form.Item label={"Emission Control"} name="emissionControl">
              <Select options={optionsSettingWasteWater} onChange={onSelectEmissionControl} allowClear />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={12}>
            <Form.Item label={"Efficiency"} name="efficiency" initialValue={0}>
              <Input maxLength={50} addonAfter={"%"} disabled />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={12} className={"date-picker-group"}>
            <CustomFormDatePicker
              name={"controlExceptionDate"}
              label={"Control Exception Date"}
              customClassName={"control-exception-date"}
              mode={"date"}
              disabledRanges={exceptionDateRanges}
            />
          </Col>

          <Col xs={24} sm={12} lg={12}>
            <Form.Item
              label={<FormLabel label={"emission"} required />}
              name="emission"
              rules={[
                {
                  required: true,
                  message: generateErrorMessage("emission"),
                },
              ]}
            >
              <Checkbox.Group options={EmissionObject} defaultValue={[EmissionObjectEnum.TVOC]} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default InformationModal;
